<template>
  <div ref="rootRef" class="language-selector">
    <button
      type="button"
      class="language-trigger core-action-button core-action-button--supporting"
      aria-haspopup="menu"
      :aria-expanded="isMenuOpen ? 'true' : 'false'"
      :title="`Language (${activeLanguageLabel})`"
      @click="toggleMenu">
      <IconWorld class="h-5 w-5" stroke="2.1" />
      <span class="language-code">{{ activeLanguageLabel }}</span>
    </button>

    <div v-if="isMenuOpen" class="language-menu" role="menu" aria-label="Language selector">
      <button
        v-for="option in languageOptions"
        :key="option.locale"
        type="button"
        class="language-menu-item"
        :class="{ active: option.locale === activeLanguage }"
        :aria-pressed="option.locale === activeLanguage"
        @click="setLanguage(option.locale)">
        <span>{{ option.label }}</span>
        <IconCheck v-if="option.locale === activeLanguage" class="h-[1.1rem] w-[1.1rem]" stroke="2" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { IconCheck, IconWorld } from '@tabler/icons-vue'
import i18n from '@/i18n'
import { useCoreSocket } from '@/services/socket/core-socket'
import { useClientStore } from '@/stores/client'
import { useProjectStore } from '@/stores/project'
import { resolveAllowedLanguage, type SupportedWidgetLanguage } from '@/helpers/shared'

type LanguageOption = {
  locale: SupportedWidgetLanguage
  label: string
}

const coreSocket = useCoreSocket()
const clientStore = useClientStore()
const projectStore = useProjectStore()
const isMenuOpen = ref(false)
const rootRef = ref<HTMLElement | null>(null)

const languageOptions = computed<LanguageOption[]>(() =>
  clientStore.available_languages.map((locale) => ({
    locale,
    label: locale.split('-')[0].toUpperCase(),
  }))
)

const activeLanguage = computed(() => {
  return resolveAllowedLanguage(String(i18n.global.locale.value || ''), clientStore.available_languages)
})

const activeLanguageLabel = computed(() => {
  return activeLanguage.value.split('-')[0].toUpperCase() || 'DE'
})

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const closeMenu = () => {
  isMenuOpen.value = false
}

const onDocumentClick = (event: MouseEvent) => {
  const target = event.target as Node | null
  if (!target || !rootRef.value?.contains(target)) {
    closeMenu()
  }
}

const onDocumentKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onDocumentKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onDocumentKeydown)
})

const setLanguage = (language: SupportedWidgetLanguage) => {
  if (!clientStore.available_languages.includes(language)) {
    closeMenu()
    return
  }

  if (language === activeLanguage.value) {
    closeMenu()
    return
  }

  i18n.global.locale.value = language
  clientStore.setManualLanguagePreference(language)

  const messageCenter = coreSocket.messageCenter
  if (messageCenter) {
    messageCenter.triggerMessage('set_language', { language })
    messageCenter.refetchLocalizedContent()
  }

  projectStore.updateProjectLanguage(language)

  closeMenu()
}
</script>

<style scoped lang="postcss">
.language-selector {
  @apply relative inline-flex;
}

.language-trigger {
  @apply min-w-[4.5rem] gap-2 rounded-none pl-4 pr-5 py-3 text-base text-neutral-550 font-semibold;
  @apply focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-default focus-visible:ring-offset-1;
}

.language-code {
  @apply text-base font-semibold;
}

.language-trigger:hover {
  @apply text-primary-dark;
}

.language-menu {
  @apply absolute bottom-full left-1/2 z-[220] mb-2 min-w-[6rem] border border-neutral-300 bg-white px-1 py-1 shadow-lg;
  animation: language-menu-fade 100ms ease-out;
  transform: translateX(-50%);
  transform-origin: bottom center;
}

.language-menu-item {
  @apply flex w-full items-center justify-between gap-2 border-0 bg-transparent px-3 py-1.5 text-left text-sm font-semibold tracking-wide text-neutral-700;
  @apply focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-default focus-visible:ring-inset;
}

.language-menu-item:hover {
  background-color: #f5f5f5;
}

.language-menu-item.active {
  @apply border text-primary-dark;
  background-color: color-mix(in srgb, var(--color-primary-default) 6%, white);
  border-color: color-mix(in srgb, var(--color-primary-default) 48%, white);
}

@keyframes language-menu-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
